import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

export const STORAGE_KEY = 'ai_scanner_onboarding_seen';

/**
 * Safely read the "seen" flag from localStorage.
 *
 * @returns {boolean}
 */
function readSeenFlag() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  } catch (error) {
    return false;
  }
}

/**
 * useOnboardingSeen
 *
 * Custom hook that tracks whether the current user has completed (or dismissed
 * via ✕) the AI Scanner onboarding coach mark. Persists the flag in
 * localStorage so returning users are recognized on the same device.
 *
 * `hasSeen` is computed fresh from localStorage on every render (reads are
 * synchronous and cheap) so it stays in sync with the persisted flag.
 *
 * A session-scoped override (`sessionSeen`) is also tracked so the flag
 * still flips to `true` in-memory even when the localStorage write itself
 * fails (e.g., quota / privacy-mode restrictions).
 *
 * @returns {{
 *   hasSeen: boolean,
 *   markSeen: () => void,
 *   userId: string
 * }}
 */
const useOnboardingSeen = () => {
  const userId = useSelector((state) => (state.user && state.user.id) || '');
  const [sessionSeen, setSessionSeen] = useState(false);

  const hasSeen = userId !== '' && (sessionSeen || readSeenFlag());

  const markSeen = useCallback(() => {
    if (!userId) {
      return;
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, 'true');
    } catch (error) {
      // Swallow quota / access errors — flag write is best-effort and the
      // session override still keeps the UI from re-showing the coach mark
      // for the rest of this session.
    }
    setSessionSeen(true);
  }, [userId]);

  return { hasSeen, markSeen, userId };
};

export default useOnboardingSeen;
